import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /**
   * Tracks the active Recharge selling plan. `undefined` = no event yet,
   * so the server-rendered default (subscription) is preserved on load.
   * @type {string|number|null|undefined}
   */
  #currentSellingPlanId = undefined;

  /**
   * Handles Recharge selling plan changes and toggles the price display.
   * @param {Event} event
   */
  #onSellingPlanChange = (event) => {
    const { sellingPlanId } = /** @type {CustomEvent} */ (event).detail || {};
    this.#currentSellingPlanId = sellingPlanId ?? null;
    this.#applySellingPlanState();
  };

  /**
   * Shows the subscription or one-time price based on the active selling plan.
   * No-op until the first Recharge event (keeps the server-rendered default).
   */
  #applySellingPlanState() {
    if (this.#currentSellingPlanId === undefined) return;
    const subEl = this.querySelector('[data-price-subscription]');
    const oneTimeEl = this.querySelector('[data-price-onetime]');
    if (!(subEl instanceof HTMLElement) || !(oneTimeEl instanceof HTMLElement)) return;
    const isSubscription = this.#currentSellingPlanId != null;
    subEl.hidden = !isSubscription;
    oneTimeEl.hidden = isSubscription;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('recharge:sellingPlanChange', this.#onSellingPlanChange);
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('recharge:sellingPlanChange', this.#onSellingPlanChange);
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Re-apply the current selling plan state to the freshly rendered price
    this.#applySellingPlanState();

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
